// Libs
import classNames from 'classnames/bind';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseTable, Loading } from '~/components/common';
// Others
import { IBaseOption, IHeaderCellType } from '~/utils/interface/common';
import { IJobDetail, IMaterialsJob, IParamsDetailJob, IServiceJob } from '~/utils/interface/job';
import { DEFAULT_CURRENCY, DEFAULT_NUMBER_ZERO, EMPTY_STRING } from '~/utils/constants/common';
import { TypeDiscountEnum, TypeParamJobDetail } from '~/utils/enum/common';
import { formatCurrency, formatDecimal } from '~/utils/helper/common';
import { getJobDetail } from '~/thunks/job/jobThunk';
import { useAppDispatch } from '~/utils/redux/hooks';
import { mockDataServiceUnit } from '~/utils/mockData/service';
// Styles, images, icons
import styles from './JobLineItems.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const columnsServices = (t: TFunction): IHeaderCellType<IServiceJob>[] => {
  return [
    {
      id: 'name',
      label: t('tab_line_items_services_table_name'),
      render: (record: IServiceJob) => record?.name || EMPTY_STRING,
    },
    {
      id: 'price',
      label: t('tab_line_items_services_table_price'),
      render: (record: IServiceJob) => (record.price ? formatCurrency(Number(record.price)) : EMPTY_STRING),
      width: 150,
    },
    {
      id: 'unit',
      label: t('tab_line_items_services_table_unit'),
      render: (record: IServiceJob) => {
        const unit: IBaseOption = mockDataServiceUnit.find(
          (item) => item.value === record?.unit
        ) as IBaseOption;

        return unit ? unit?.label : EMPTY_STRING;
      },
      width: 150,
    },
    {
      id: 'quantity',
      label: t('tab_line_items_services_table_quantity'),
      render: (record: IServiceJob) =>
        record?.quantity ? formatDecimal(Number(record?.quantity)) : DEFAULT_NUMBER_ZERO,
      width: 150,
    },
  ];
};

const columnsMaterials = (t: TFunction): IHeaderCellType<IMaterialsJob>[] => {
  return [
    {
      id: 'name',
      label: t('tab_line_items_materials_table_name'),
      render: (record: IMaterialsJob) => record?.name || EMPTY_STRING,
    },
    {
      id: 'price',
      label: t('tab_line_items_materials_table_price'),
      render: (record: IMaterialsJob) => (record.price ? formatCurrency(Number(record.price)) : EMPTY_STRING),
      width: 150,
    },
    {
      id: 'unit',
      label: t('tab_line_items_materials_table_unit'),
      render: (record: IMaterialsJob) => {
        const unit: IBaseOption = mockDataServiceUnit.find(
          (item) => item.value === record?.unit
        ) as IBaseOption;

        return unit ? unit?.label : EMPTY_STRING;
      },
      width: 150,
    },
    {
      id: 'quantity',
      label: t('tab_line_items_materials_table_quantity'),
      render: (record: IMaterialsJob) =>
        record?.quantity ? formatDecimal(Number(record?.quantity)) : DEFAULT_NUMBER_ZERO,
      width: 150,
    },
  ];
};

const JobLineItems = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/job']);
  const { id: jobId } = useParams();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [jobDetail, setJobDetail] = useState<IJobDetail>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!jobId) return;
    const params: IParamsDetailJob = {
      jobId,
      type: TypeParamJobDetail.LINE_ITEM,
    };

    handleGetJobDetail(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetJobDetail = (params: IParamsDetailJob) => {
    setIsLoading(true);

    dispatch(getJobDetail(params))
      .unwrap()
      .then((res) => {
        const { data } = res;
        if (!data) return;

        setJobDetail(data as IJobDetail);
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const calculateSubtotal = () => {
    if (!jobDetail) return DEFAULT_NUMBER_ZERO;

    const totalServices = jobDetail?.services?.reduce(
      (sum, service) => sum + (Number(service.totalPrice) || DEFAULT_NUMBER_ZERO),
      DEFAULT_NUMBER_ZERO
    );

    const totalMaterials = jobDetail?.materials?.reduce(
      (sum, material) => sum + (Number(material.totalPrice) || DEFAULT_NUMBER_ZERO),
      DEFAULT_NUMBER_ZERO
    );

    return totalServices + totalMaterials || DEFAULT_NUMBER_ZERO;
  };

  const calculateDiscount = () => {
    if (!jobDetail) return DEFAULT_NUMBER_ZERO;

    const total = calculateSubtotal();

    const { totalPercentage, totalDollar } = (jobDetail.discounts || []).reduce(
      (acc, discount) => {
        const rate = Number(discount.rate) || DEFAULT_NUMBER_ZERO;
        if (discount.type === TypeDiscountEnum.PERCENTAGE) {
          acc.totalPercentage += rate;
        } else if (discount.type === TypeDiscountEnum.DOLLAR) {
          acc.totalDollar += rate;
        }
        return acc;
      },
      { totalPercentage: DEFAULT_NUMBER_ZERO, totalDollar: DEFAULT_NUMBER_ZERO }
    );

    return (Number(total) * totalPercentage) / 100 + totalDollar;
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='jobLineItemsPage' className={cx('container')}>
      <Box component={'div'} className={cx('viewBody')}>
        <Box component={'div'} className={cx('body')}>
          <Typography component={'div'} className={cx('title')}>
            {t('tab_line_items_services_title')}
          </Typography>
          <BaseTable columns={columnsServices(t)} dataSource={jobDetail?.services || []} />
        </Box>

        <Box component={'div'} className={cx('body')}>
          <Typography component={'div'} className={cx('title')}>
            {t('tab_line_items_materials_title')}
          </Typography>
          <BaseTable columns={columnsMaterials(t)} dataSource={jobDetail?.materials || []} />
        </Box>
      </Box>

      <Stack direction='row' className={cx('viewTotal')}>
        <Box className={cx('item')}>
          <Box className={cx('textTitle')}>{t('field_subtotal_label')}</Box>
          <Box className={cx('textSubtotal')}>{formatCurrency(calculateSubtotal())}</Box>
        </Box>

        <Box className={cx('item')}>
          <Box className={cx('textTitle')}>{t('field_discount_label')}</Box>
          <Box className={cx('textDiscount')}>{`-${formatCurrency(calculateDiscount())}`}</Box>
        </Box>

        <Box className={cx('item')}>
          <Box className={cx('textTitleTotal')}>{t('field_total_label')}</Box>
          <Box className={cx('textSubtotal')}>
            {jobDetail?.total ? formatCurrency(jobDetail?.total) : DEFAULT_CURRENCY}
          </Box>
        </Box>
      </Stack>

      {isLoading && <Loading />}
    </Box>
  );
};

export default JobLineItems;
